/**
 * Tile 2: Availability Editor UI Mock (Host View)
 * Shows pricing + blocked dates configuration
 */
const TileAvailabilityCalendar = () => {
  const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  // Calendar grid: 0 = empty, 1 = available with price, 2 = blocked
  const calendarData = [
    [0, 0, 0, 1, 1, 1, 2], // First row (partial week)
    [2, 1, 1, 1, 1, 1, 1], // Second row
    [1, 1, 2, 2, 2, 1, 1], // Third row (blocked range in middle)
    [1, 1, 1, 1, 1, 2, 1], // Fourth row
    [1, 1, 0, 0, 0, 0, 0], // Fifth row (partial)
  ];

  const dayNumbers = [
    [0, 0, 0, 1, 2, 3, 4],
    [5, 6, 7, 8, 9, 10, 11],
    [12, 13, 14, 15, 16, 17, 18],
    [19, 20, 21, 22, 23, 24, 25],
    [26, 27, 0, 0, 0, 0, 0],
  ];

  const prices: Record<number, string> = {
    1: '€120', 2: '€120', 3: '€120', 6: '€95', 7: '€95', 8: '€95',
    9: '€95', 10: '€95', 11: '€95', 12: '€95', 13: '€95',
    17: '€120', 18: '€120', 19: '€95', 20: '€95', 21: '€95',
    22: '€95', 23: '€95', 25: '€120', 26: '€95', 27: '€95',
  };

  const getCellStyle = (type: number): string => {
    const base = 'aspect-square flex flex-col items-center justify-center text-[10px] rounded';
    switch (type) {
      case 0: return base + ' bg-transparent';
      case 1: return base + ' bg-white'; // available
      case 2: return base + ' bg-neutral-100'; // blocked
      default: return base;
    }
  };

  return (
    <div className="border border-neutral-200 bg-neutral-50 rounded-lg p-4 space-y-3">
      {/* Editor header */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-neutral-600">Availability</span>
        <span className="text-[10px] text-neutral-500 border border-neutral-200 rounded-full px-2 py-0.5 bg-white">
          Edit
        </span>
      </div>

      {/* Rules row */}
      <div className="flex items-center gap-3 text-[10px] text-neutral-500">
        <span>Nightly: <span className="text-neutral-700 font-medium">€120</span></span>
        <span className="text-neutral-300">|</span>
        <span>Min stay: <span className="text-neutral-700 font-medium">3 nights</span></span>
      </div>

      {/* Month header */}
      <div className="flex items-center justify-between">
        <button type="button" aria-label="Previous month" className="w-5 h-5 flex items-center justify-center text-neutral-400 hover:text-neutral-600">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-xs font-semibold text-neutral-700">January 2026</span>
        <button type="button" aria-label="Next month" className="w-5 h-5 flex items-center justify-center text-neutral-400 hover:text-neutral-600">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7">
        {weekdays.map((day, i) => (
          <div key={i} className="text-center text-[9px] font-medium text-neutral-400 py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-0.5">
        {calendarData.map((row, rowIndex) =>
          row.map((cellType, colIndex) => {
            const dayNum = dayNumbers[rowIndex][colIndex];
            const isBlocked = cellType === 2;
            const isAvailable = cellType === 1;
            const price = prices[dayNum];

            return (
              <div key={`${rowIndex}-${colIndex}`} className={getCellStyle(cellType)}>
                {dayNum > 0 && (
                  <>
                    <span className={`font-medium ${isBlocked ? 'text-neutral-400' : 'text-neutral-700'}`}>
                      {dayNum}
                    </span>
                    {isAvailable && price ? (
                      <span className="text-[8px] text-neutral-500">{price}</span>
                    ) : isBlocked ? (
                      <span className="text-[8px] text-neutral-400">—</span>
                    ) : null}
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 pt-2 border-t border-neutral-200">
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 bg-white border border-neutral-200 rounded-sm" />
          <span className="text-[9px] text-neutral-500">Available</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 bg-neutral-100 border border-neutral-200 rounded-sm" />
          <span className="text-[9px] text-neutral-500">Blocked</span>
        </div>
      </div>
    </div>
  );
};

export default TileAvailabilityCalendar;
